import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  doc,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";

const MAX_APPROVED_PER_DAY = 4;

export default function NotificationBell({ enabled = true }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState([]);

  useEffect(() => {
    if (!enabled) return;
    const qRef = query(
      collection(db, "timeOffRequests"),
      where("status", "==", "pending")
    );
    const unsub = onSnapshot(qRef, (snap) => {
      setPending(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [enabled]);

  async function approve(req) {
    const reqRef = doc(db, "timeOffRequests", req.id);
    const bucketRef = doc(db, "dayBuckets", req.date);

    try {
      await runTransaction(db, async (tx) => {
        const reqSnap = await tx.get(reqRef);
        if (!reqSnap.exists()) throw new Error("Request missing.");
        const data = reqSnap.data();

        if ((data.status || "").toLowerCase() !== "pending") {
          throw new Error("Only pending can be approved.");
        }

        const bucketSnap = await tx.get(bucketRef);
        const bucket = bucketSnap.exists()
          ? bucketSnap.data()
          : { approvedUids: {}, pendingUids: {} };

        const approvedUids = bucket.approvedUids || {};
        const pendingUids = bucket.pendingUids || {};

        const approvedCount = Object.keys(approvedUids).length;
        if (approvedCount >= MAX_APPROVED_PER_DAY) {
          throw new Error("Day already has 4 approved.");
        }

        delete pendingUids[data.uid];
        approvedUids[data.uid] = true;

        tx.set(
          bucketRef,
          { approvedUids, pendingUids, updatedAt: serverTimestamp() },
          { merge: true }
        );

        tx.update(reqRef, { status: "approved", approvedAt: serverTimestamp() });
      });
    } catch (err) {
      console.error(err);
      alert(err?.message || "Could not approve.");
    }
  }

  async function deny(req) {
    const reqRef = doc(db, "timeOffRequests", req.id);
    const bucketRef = doc(db, "dayBuckets", req.date);

    try {
      await runTransaction(db, async (tx) => {
        const reqSnap = await tx.get(reqRef);
        if (!reqSnap.exists()) throw new Error("Request missing.");
        const data = reqSnap.data();

        if ((data.status || "").toLowerCase() !== "pending") {
          throw new Error("Only pending can be denied.");
        }

        const bucketSnap = await tx.get(bucketRef);
        const bucket = bucketSnap.exists()
          ? bucketSnap.data()
          : { approvedUids: {}, pendingUids: {} };

        const approvedUids = bucket.approvedUids || {};
        const pendingUids = bucket.pendingUids || {};

        delete pendingUids[data.uid];

        tx.set(
          bucketRef,
          { approvedUids, pendingUids, updatedAt: serverTimestamp() },
          { merge: true }
        );

        tx.update(reqRef, { status: "denied", deniedAt: serverTimestamp() });
      });
    } catch (err) {
      console.error(err);
      alert(err?.message || "Could not deny.");
    }
  }

  if (!enabled) return null;

  return (
    <div className="relative">
      <button
        className="relative p-2 rounded hover:bg-blue-700/20"
        onClick={() => setOpen((o) => !o)}
        title="Pending approvals"
      >
        🔔
        {pending.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full text-[10px] w-5 h-5 flex items-center justify-center">
            {pending.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[360px] max-h-[70vh] overflow-auto bg-white border rounded shadow-lg p-3 z-50">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold">Pending Requests</div>
            <button className="text-xs opacity-70" onClick={() => setOpen(false)}>
              Close
            </button>
          </div>

          {pending.length === 0 ? (
            <div className="text-sm text-gray-800">No pending requests.</div>
          ) : (
            <div className="space-y-2">
              {pending.map((r) => (
                <div key={r.id} className="border rounded p-2 text-sm bg-gray-50">
                  <div className="font-semibold text-gray-800">
                    {r.email} <span className="text-gray-800">• {r.date}</span>
                  </div>
                  <div className="text-xs text-gray-800">{r.reason} • {r.start}–{r.end}</div>
                 
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
